import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import {
  CancelOutlined,
  CheckCircleOutline,
  DisabledByDefault,
  DownloadForOffline,
  HourglassBottom,
  Preview,
} from "@mui/icons-material";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useSelector } from "react-redux";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { API_ENDPOINT } from "../../../services/api/resources";
import classes from "./Invoice.module.css";

const ApproverInvoice = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const user_token = loginData?.user?.token || loginData?.user_token;
  const [vendorinvoicedata, setVendorInvoiceData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [payeeStatusFilter, setPayeeStatusFilter] = useState("All");
  const [vendorStatusFilter, setVendorStatusFilter] = useState("All");

  const showDialog = (message) => {
    setDialogMessage(message);
    setDialogOpen(true);
  };

  const InvoiceFetch = async (pageNo = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const res = await axios.post(API_ENDPOINT.get_all_vendor_invoices, {
        org_token: user_token,
      });
      setVendorInvoiceData(res.data.results.invoices || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    InvoiceFetch(1, rowsPerPage);
  }, []);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    InvoiceFetch(newPage + 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    InvoiceFetch(1, newSize);
  };

  const downloadPDF = (invoice) => {
    if (!invoice?.pdf_url) {
      showDialog("Invoice PDF not available");
      return;
    }
    const a = document.createElement("a");
    a.href = invoice.pdf_url;
    a.download = "Invoice.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openPDFInNewTab = (invoice) => {
    if (!invoice?.pdf_url) {
      showDialog("Invoice PDF not available");
      return;
    }
    window.open(invoice.pdf_url, "_blank");
  };

  const handleApprove = async (invoice_token, action, role) => {
    try {
      const payload = { invoice_token, action };
      if (role === "account") payload.is_account = true;
      if (role === "business") payload.is_business = true;

      await axios.post(API_ENDPOINT.approve_vendor_invoice, payload);

      showDialog(
        `Invoice ${action === "accept" ? "Approved" : "Rejected"} by ${
          role === "account" ? "Accounts Payable" : "Business Finance"
        } Team`
      );
      InvoiceFetch(page + 1, rowsPerPage);
    } catch (err) {
      console.error(err);
      showDialog("Failed to approve invoice.");
    }
  };

  const handleBulkApproveReject = async (action) => {
    try {
      const payload = {
        invoice_tokens: selectedInvoices,
        action,
        ...(loginData?.user?.designation === "Account Payable" && {
          is_account: true,
        }),
        ...(loginData?.user?.designation === "Business Finance" && {
          is_business: true,
        }),
      };
      await axios.post(API_ENDPOINT.bulk_approve_vendor_invoices, payload);
      showDialog(
        `Selected invoices have been ${
          action === "accept" ? "approved" : "rejected"
        }.`
      );
      setSelectedInvoices([]);
      InvoiceFetch(page + 1, rowsPerPage);
    } catch (err) {
      console.error("Bulk action failed", err);
      showDialog("Failed to process bulk action.");
    }
  };
  const filteredData = vendorinvoicedata.filter((item) => {
    const payeeStatus =
      item.finance_approved === 1
        ? "Approved"
        : item.finance_approved === 2
        ? "Pending"
        : "Cancelled";

    const vendorStatus =
      item.status === 1
        ? "Approved"
        : item.status === 2
        ? "Pending"
        : "Cancelled";

    const matchesSearch =
      item.vendor_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.amount?.toString().includes(searchText);

    const matchesPayee =
      payeeStatusFilter === "All" || payeeStatus === payeeStatusFilter;

    const matchesVendor =
      vendorStatusFilter === "All" || vendorStatus === vendorStatusFilter;

    return matchesSearch && matchesPayee && matchesVendor;
  });

  console.log(vendorinvoicedata);
  return (
    <div style={{ margin: "1rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <HeaderTitle titleText="Invoice Approval Status" />

        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          {/* Payee Status Filter */}
          <select
            value={payeeStatusFilter}
            onChange={(e) => setPayeeStatusFilter(e.target.value)}
            style={{
              padding: "10px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              outline: "none",
              width: "150px",
            }}
          >
            <option value="All">Payee: All</option>
            <option value="Approved">Payee: Approved</option>
            <option value="Pending">Payee: Pending</option>
            <option value="Cancelled">Payee: Rejected</option>
          </select>

          {/* Vendor / Merchant Status Filter */}
          <select
            value={vendorStatusFilter}
            onChange={(e) => setVendorStatusFilter(e.target.value)}
            style={{
              padding: "10px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              outline: "none",
              width: "180px",
            }}
          >
            <option value="All">Vendor: All</option>
            <option value="Approved">Vendor: Approved</option>
            <option value="Pending">Vendor: Pending</option>
            <option value="Cancelled">Vendor: Rejected</option>
          </select>

          {/* Search Box */}
          <input
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: "10px 12px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              width: "250px",
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#1976d2")}
            onBlur={(e) => (e.target.style.borderColor = "#ccc")}
          />
        </div>
      </div>

      {selectedInvoices.length > 0 && (
        <div style={{ marginBottom: "1rem", display: "flex", gap: "10px" }}>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleBulkApproveReject("accept")}
          >
            Approve Selected
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleBulkApproveReject("reject")}
          >
            Reject Selected
          </Button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <CircularProgress />
        </div>
      ) : (
        <TableContainer style={{ background: "#fff" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={
                      vendorinvoicedata.length > 0 &&
                      vendorinvoicedata.every((item) =>
                        selectedInvoices.includes(item.invoice_token)
                      )
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newTokens = vendorinvoicedata.map(
                          (item) => item.invoice_token
                        );
                        const updated = Array.from(
                          new Set([...selectedInvoices, ...newTokens])
                        );
                        setSelectedInvoices(updated);
                      } else {
                        const filtered = selectedInvoices.filter(
                          (token) =>
                            !vendorinvoicedata.some(
                              (item) => item.invoice_token === token
                            )
                        );
                        setSelectedInvoices(filtered);
                      }
                    }}
                  />
                </TableCell>
                <TableCell style={{ fontSize: "16px", whiteSpace: "nowrap" }}>
                  <strong>Vendor / Merchant Name</strong>
                </TableCell>
                <TableCell style={{ fontSize: "16px", whiteSpace: "nowrap" }}>
                  <strong>Amount (₹)</strong>
                </TableCell>
                <TableCell style={{ fontSize: "16px", whiteSpace: "nowrap" }}>
                  <strong>Invoice</strong>
                </TableCell>
                <TableCell style={{ fontSize: "16px", whiteSpace: "nowrap" }}>
                  <strong>Action</strong>
                </TableCell>
                <TableCell style={{ fontSize: "16px", whiteSpace: "nowrap" }}>
                  <strong>Payee's Status</strong>
                </TableCell>
                <TableCell style={{ fontSize: "16px", whiteSpace: "nowrap" }}>
                  <strong>Vendor / Merchant Status</strong>
                </TableCell>
                <TableCell style={{ fontSize: "16px", whiteSpace: "nowrap" }}>
                  <strong>Raised On</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((items) => (
                <TableRow key={items.invoice_token}>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.includes(items.invoice_token)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInvoices([
                            ...selectedInvoices,
                            items.invoice_token,
                          ]);
                        } else {
                          setSelectedInvoices(
                            selectedInvoices.filter(
                              (token) => token !== items.invoice_token
                            )
                          );
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ fontSize: "14px" }}>
                    {items.vendor_name}
                  </TableCell>
                  <TableCell style={{ fontSize: "14px" }}>
                    ₹ {items.amount?.toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell style={{ fontSize: "14px", whiteSpace: "nowrap" }}>
                    <DownloadForOffline
                      fontSize="medium"
                      style={{
                        cursor: "pointer",
                        marginRight: 8,
                        fontSize: "25px",
                      }}
                      onClick={() => downloadPDF(items)}
                    />
                    <Preview
                      fontSize="medium"
                      style={{ cursor: "pointer", fontSize: "25px" }}
                      onClick={() => openPDFInNewTab(items)}
                    />
                  </TableCell>
                  <TableCell style={{ fontSize: "15px", whiteSpace: "nowrap" }}>
                    {loginData?.user?.designation === "Account Payable" && (
                      <>
                        {items.account_approved === 1 ? (
                          <Button variant="contained" disabled>
                            Approved
                          </Button>
                        ) : items.account_approved === 3 ? (
                          <Button variant="outlined" disabled color="error">
                            Rejected
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="contained"
                              onClick={() =>
                                handleApprove(
                                  items.invoice_token,
                                  "accept",
                                  "account"
                                )
                              }
                              style={{ marginRight: "8px" }}
                            >
                              Approve
                            </Button>

                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleApprove(
                                  items.invoice_token,
                                  "reject",
                                  "account"
                                )
                              }
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </>
                    )}

                    {loginData?.user?.designation === "Business Finance" && (
                      <>
                        {items.finance_approved === 1 ? (
                          <Button variant="contained" disabled>
                            Approved
                          </Button>
                        ) : items.finance_approved === 3 ? (
                          <Button variant="outlined" disabled color="error">
                            Rejected
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="contained"
                              onClick={() =>
                                handleApprove(
                                  items.invoice_token,
                                  "accept",
                                  "business"
                                )
                              }
                              style={{ marginRight: "8px" }}
                            >
                              Approve
                            </Button>

                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleApprove(
                                  items.invoice_token,
                                  "reject",
                                  "business"
                                )
                              }
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </TableCell>

                  {loginData?.user?.designation === "Business Finance" && (
                    <TableCell
                      className={classes.status}
                      style={{ fontSize: "15px" }}
                    >
                      {items.finance_approved === 1 ? (
                        <span
                          style={{
                            color: "green",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "12px",
                          }}
                        >
                          <VerifiedIcon /> Approved
                        </span>
                      ) : items.finance_approved === 2 ? (
                        <span
                          style={{
                            color: "orange",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "12px",
                          }}
                        >
                          <HourglassBottom /> Pending
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "red",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "12px",
                          }}
                        >
                          <DisabledByDefault /> Rejected
                        </span>
                      )}
                    </TableCell>
                  )}

                  {loginData?.user?.designation === "Account Payable" && (
                    <TableCell className={classes.status}>
                      {items.account_approved === 1 ? (
                        <span
                          style={{
                            color: "green",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "12px",
                          }}
                        >
                          <VerifiedIcon /> Approved
                        </span>
                      ) : items.account_approved === 2 ? (
                        <span
                          style={{
                            color: "orange",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "12px",
                          }}
                        >
                          <HourglassBottom /> Pending
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "red",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "12px",
                          }}
                        >
                          <DisabledByDefault /> Rejected
                        </span>
                      )}
                    </TableCell>
                  )}

                  <TableCell className={classes.status}>
                    {items.status === 1 ? (
                      <span
                        style={{
                          color: "green",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "12px",
                        }}
                      >
                        <VerifiedIcon /> Approved
                      </span>
                    ) : items.status === 2 ? (
                      <span
                        style={{
                          color: "orange",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "12px",
                        }}
                      >
                        <HourglassBottom /> Pending
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "red",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "12px",
                        }}
                      >
                        <DisabledByDefault /> Rejected
                      </span>
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "15px" }}>
                    {new Date(items.created_on).toLocaleDateString("en-IN")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sx={{
              "& .MuiTablePagination-toolbar": { fontSize: "14px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                { fontSize: "14px" },
              "& .MuiInputBase-root": { fontSize: "14px" },
            }}
          />
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "16px" }}>
          Status
        </DialogTitle>
        <DialogContent>
          <Typography variant="h5">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDialogOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ApproverInvoice;
